<template>
  <section class="main">
    <loader v-if="loading" />
    <template v-else>
      <h1 class="intro">
        <span class="intro__inner">{{ $t('P2P_SIGNING_MARKETPLACE') }}</span>
      </h1>
      <div class="row">
        <div class="col col_1">
          <important-message v-if="hasPremium" type="success">
            {{ $t('PREMIUM_ALREADY_ACTIVE') }}
          </important-message>

          <html-with-links class="text g-text" :html="$t('P2P_SIGNING_MARKETPLACE_DESC')" />

          <template v-if="plusAccount && plusAccount.is_yours === 1">
            <html-with-links v-if="plusAccount.status !== 'ok'" tag="p" class="text status g-text-error"
              :html="$t('P2P_ACCOUNT_STATUS_NOT_OK', { devAccountLink: '/my/apple' })" />

            <template v-else>
              <btn v-if="!configuringP2PSettings" type="primary" style="margin-top: 10px;"
                @click.native="configuringP2PSettings = true">
                {{ $t('CONFIGURE') }}
              </btn>
              <template v-else>
                <table class="table table_configure">
                  <tr>
                    <td class="cell">
                      {{ $t('STATUS') }}
                    </td>
                    <td class="td">
                      <yes-no v-model="p2pSettings.isP2P"
                        :options="[{ label: $t('DISABLED'), value: 0 }, { label: $t('ENABLED'), value: 1 }]" />
                    </td>
                  </tr>
                  <tr>
                    <td class="cell">
                      {{ $t('YOUR_NAME') }} *
                    </td>
                    <td class="td">
                      <input class="input" v-model="p2pSettings.p2pName" />
                    </td>
                  </tr>
                  <tr>
                    <td class="cell">
                      {{ $t('PRICE_PER_DEVICE') }} *
                    </td>
                    <td class="td">
                      <input class="input" v-model="p2pSettings.p2pPrice" />
                    </td>
                  </tr>
                  <tr>
                    <td class="cell">
                      {{ $t('CURRENCY') }} *
                    </td>
                    <td class="td">
                      <v-select style="width: 150px;" v-model="p2pSettings.p2pCurrency" :options="currencySelectOptions"
                        :clearable="false" compact />
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <p style="max-width: 330px; padding-bottom: 10px;" v-html="$t('P2P_CURRENCY_HINT')"></p>
                    </td>
                  </tr>
                  <tr>
                    <td class="cell">
                      PayPal email
                    </td>
                    <td class="td">
                      <input class="input" v-model="p2pSettings.p2pPaypalEmail" />
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <p style="max-width: 330px; padding-bottom: 10px;" v-html="$t('P2P_PAYPAL_HINT')"></p>
                    </td>
                  </tr>
                  <tr>
                    <td class="cell">
                      {{ $t('MANUAL_INSTRUCTIONS') }}
                    </td>
                    <td class="td">
                      <textarea class="textarea" rows="5" v-model="p2pSettings.p2pManualInstructions" />
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <p style="max-width: 480px; padding-bottom: 10px;"
                        v-html="$t('P2P_MANUAL_INSTRUCTIONS_HINT', { email })"></p>
                    </td>
                  </tr>
                </table>
                <p class="text g-text"
                  v-html="$t('P2P_SETTINGS_LEGAL_TEXT', { devicesLimit: stats.p2p_devices_limit })"></p>
                <btn type="primary" @click.native="saveP2PSettings" style="margin-top: 10px;"
                  :loading="savingP2PSettings">{{ $t('SAVE') }}
                </btn>
              </template>
            </template>
          </template>


          <html-with-links v-if="plusAccount && plusAccount.is_yours !== 1 && plusAccount.status === 'ok'"
            class="text g-text status" :tag="p" :html="$t('YOU_DONT_NEED_P2P_CERTIFICATE')"></html-with-links>
          <html-with-links v-if="plusAccount && plusAccount.is_yours !== 1 && plusAccount.status === 'p2p_pending'"
            class="text g-text status" :tag="p"
            :html="$t('P2P_PENDING_STATUS_DESCRIPTION', { 'href': '/my/apple' })"></html-with-links>
          <html-with-links v-if="plusAccount && plusAccount.is_yours !== 1 && plusAccount.status === 'p2p_rejected'"
            class="text g-text status" :tag="p"
            :html="$t('P2P_REJECTED_STATUS_DESCRIPTION', { 'href': '/my/apple' })"></html-with-links>

          <template v-if="purchases.length">
            <h2 class="intro">
              {{
                $t('SALES')
              }}
            </h2>

            <template v-if="purchases.length">
              <div v-if="!isMobile" class="table-apps-wrapper">
                <table class="table-apps">
                  <thead class="table-apps__head table-apps__row">
                    <tr>
                      <th class="table-apps__cell">UDID</th>
                      <th class="table-apps__cell">{{ $t('PURCHASE_DATE') }}</th>
                      <th class="table-apps__cell">{{ $t('REMOVAL_DATE') }}</th>
                      <th class="table-apps__cell">{{ $t('STATUS') }}</th>
                      <th class="table-apps__cell">{{ $t('ACTIONS') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="table-apps__row" v-for="purchase in purchases" :key="purchase.udid">
                      <td class="table-apps__cell">{{ purchase.udid }}</td>
                      <td class="table-apps__cell">
                        {{ new Date(purchase.added_at * 1000).toLocaleDateString() }}
                      </td>
                      <td class="table-apps__cell">
                        {{ new Date(purchase.expires_at * 1000).toLocaleDateString() }}
                      </td>
                      <td class="table-apps__cell">
                        {{ purchase.status_translated }}
                      </td>
                      <td class="table-apps__cell">
                        <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                          <template v-if="purchase.status === 'ok'">
                            <template v-if="!purchase.is_banned">
                              <btn type="negative" :loading="banningPurchases.includes(purchase.udid)"
                                @click.native="banPurchase(purchase.udid)">
                                {{ $t('BAN') }}
                              </btn>
                            </template>
                            <template v-else>
                              <btn type="primary" :loading="unbanningPurchases.includes(purchase.udid)"
                                @click.native="unbanPurchase(purchase.udid)">
                                {{ $t('UNBAN') }}
                              </btn>
                            </template>
                          </template>
                          <btn v-if="purchase.status === 'pending' || purchase.status === 'rejected'" type="primary"
                            :loading="approvingPurchases.includes(purchase.id)"
                            @click.native="approvePurchase(purchase.id)">
                            {{ $t('APPROVE') }}
                          </btn>
                          <btn v-if="purchase.status === 'pending'" type="negative"
                            :loading="rejectingPurchases.includes(purchase.id)"
                            @click.native="rejectPurchase(purchase.id)">
                            {{ $t('REJECT') }}
                          </btn>
                          <btn tag="router-link" :to="`/p2p/discussion?id=${purchase.id}`">
                            {{ $t('CHAT') }}
                          </btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="commands">
                <div class="command" v-for="purchase in purchases" :key="purchase.udid">
                  <span class="command__heading">UDID</span>
                  <span>
                    {{ purchase.udid }}
                  </span>
                  <span class="command__heading">{{ $t('PURCHASE_DATE') }}</span>
                  <span>{{ new Date(purchase.added_at * 1000).toLocaleDateString() }}</span>
                  <span class="command__heading">{{ $t('REMOVAL_DATE') }}</span>
                  <span>{{ new Date(purchase.expires_at * 1000).toLocaleDateString() }}</span>
                  <span class="command__heading">{{ $t('STATUS') }}</span>
                  <span>{{ purchase.status_translated }}</span>
                  <span class="command__heading"></span>
                  <span>
                    <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                      <template v-if="purchase.status === 'ok'">
                        <template v-if="!purchase.is_banned">
                          <btn type="negative" :loading="banningPurchases.includes(purchase.udid)"
                            @click.native="banPurchase(purchase.udid)">
                            {{ $t('BAN') }}
                          </btn>
                        </template>
                        <template v-else>
                          <btn type="primary" :loading="unbanningPurchases.includes(purchase.udid)"
                            @click.native="unbanPurchase(purchase.udid)">
                            {{ $t('UNBAN') }}
                          </btn>
                        </template>
                      </template>
                      <btn v-if="purchase.status === 'pending' || purchase.status === 'rejected'" type="primary"
                        :loading="approvingPurchases.includes(purchase.id)"
                        @click.native="approvePurchase(purchase.id)">
                        {{ $t('APPROVE') }}
                      </btn>
                      <btn v-if="purchase.status === 'pending'" type="negative"
                        :loading="rejectingPurchases.includes(purchase.id)" @click.native="rejectPurchase(purchase.id)">
                        {{ $t('REJECT') }}
                      </btn>
                      <btn tag="router-link" :to="`/p2p/discussion?id=${purchase.id}`">
                        {{ $t('CHAT') }}
                      </btn>
                    </div>
                  </span>
                </div>
              </div>
            </template>
            <p v-else>{{ $t('NOTHING_WAS_FOUND') }}</p>
          </template>

          <h2 class="intro">
            {{
              $t('OFFERS')
            }}
          </h2>
          <div class="currency-select">
            <strong>{{ $t('FILTER_BY_CURRENCY') }}</strong>

            <v-select v-model="offersCurrency"
              :options="[{ label: $t('NOT_SELECTED'), value: null }, ...currencySelectOptions]" :clearable="false"
              compact />
          </div>

          <div v-if="!isMobile" class="table-apps-wrapper">
            <table class="table-apps">
              <thead class="table-apps__head table-apps__row">
                <tr>
                  <th class="table-apps__cell">{{ $t('PRICE') }}</th>
                  <th class="table-apps__cell">{{ $t('NAME') }}</th>
                  <th class="table-apps__cell">{{ $t('EXPIRES_AT') }}</th>
                  <th class="table-apps__cell">{{ $t('REINSTALL_DATE') }}*</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr class="table-apps__row" v-for="offer in offers" :key="offer.id">
                  <td class="table-apps__cell">
                    {{ offer.price }}
                    {{ offer.currency }}
                  </td>
                  <td class="table-apps__cell">{{ offer.name }}</td>
                  <td class="table-apps__cell">
                    {{ new Date(offer.expires_at * 1000).toLocaleString() }}
                  </td>
                  <td class="table-apps__cell">
                    {{
                      offer.reinstallation_at ?
                        new Date(offer.reinstallation_at * 1000).toLocaleString() : 'N/A'
                    }}
                  </td>
                  <td class="table-apps__cell table-apps__cell_nw table-apps__cell_right">
                    <template v-if="!plusAccount || plusAccount.is_yours !== 1">
                      <btn type="primary" :loading="confirmingOffers.includes(offer.id)" @click.native="buy(offer)">
                        <template v-if="offer.manual_instructions">
                          {{ $t('BUY_MANUALLY') }}
                        </template>
                        <template v-else-if="offer.purchase_uri">
                          {{ $t('BUY_WITH_PAYPAL') }}
                        </template>
                        <template v-else>
                          {{ $t('BUY') }}
                        </template>
                      </btn>

                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="commands">
            <div class="command" v-for="offer in offers" :key="offer.id">
              <span class="command__heading">{{ $t('PRICE') }}</span>
              <span>
                {{ offer.price }}
                {{ offer.currency }}
                <template v-if="!plusAccount || plusAccount.is_yours !== 1">
                  <btn type="primary" style="margin-left: 4px;" :loading="confirmingOffers.includes(offer.id)"
                    @click.native="buy(offer)">
                    <template v-if="offer.manual_instructions">
                      {{ $t('BUY_MANUALLY') }}
                    </template>
                    <template v-else-if="offer.purchase_uri">
                      {{ $t('BUY_WITH_PAYPAL') }}
                    </template>
                    <template v-else>
                      {{ $t('BUY') }}
                    </template>
                  </btn>
                </template>
              </span>
              <span class="command__heading">{{ $t('NAME') }}</span>
              <span>{{ offer.name }}</span>
              <span class="command__heading">{{ $t('EXPIRES_AT') }}</span>
              <span>{{ new Date(offer.expires_at * 1000).toLocaleString() }}</span>
              <span class="command__heading">{{ $t('REINSTALL_DATE') }}*</span>
              <span>{{
                offer.reinstallation_at ?
                  new Date(offer.reinstallation_at * 1000).toLocaleString() : 'N/A'
              }}</span>
            </div>
          </div>

          <p v-if="!offers.length" style="margin-top: 15px;">{{ $t('NOTHING_WAS_FOUND') }}</p>
          <html-with-links v-else tag="p" style="margin-top: 15px;" class="text g-text"
            :html="`*${$t('P2P_OFFERS_REINSTALL_DATE_DESC')}`" />
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import Btn from '@/components/Button';
import Loader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks';
import YesNo from '@/components/YesNo.vue';
import VSelect from '@/components/VSelect.vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import isItselfLinkable from '@/utils/isItselfLinkable';
import ImportantMessage from '@/components/ImportantMessage.vue';

export default {
  components: {
    Btn,
    Loader,
    HtmlWithLinks,
    YesNo,
    VSelect,
    ImportantMessage,
  },
  data() {
    return {
      isMobile: window.innerWidth <= 767 || window.innerHeight <= 767,
      loading: true,
      offers: [],
      offersCurrency: null,
      purchases: [],
      configuringP2PSettings: false,
      savingP2PSettings: false,
      p2pSettings: {
        isP2P: '0',
        p2pPrice: '',
        p2pName: '',
        p2pPaypalEmail: '',
        p2pCurrency: '',
        p2pManualInstructions: '',
      },
      banningPurchases: [],
      unbanningPurchases: [],
      approvingPurchases: [],
      rejectingPurchases: [],
      confirmingOffers: [],
    };
  },
  computed: {
    ...mapState(['devices', 'lt', 'stats', 'email']),
    ...mapGetters(['hasPlus', 'config', 'isDeviceLinked', 'devices', 'lt', 'hasPremium']),
    currentDevice() {
      return this.devices.find(d => d.lt === this.lt) || {};
    },
    title() {
      return this.$t('P2P_SIGNING_MARKETPLACE');
    },
    configLoaded() {
      return !!this.config.model || (this.devicesLoaded && !this.isDeviceLinked);
    },
    plusAccount() {
      return this.config.plus_account;
    },
    currencySelectOptions() {
      return Object.keys(this.stats.p2p_currencies)
        .map(key => ({
          label: key,
          value: key,
        }));
    },
  },
  methods: {
    ...mapActions(['getStats']),
    async buy(offer) {
      if (offer.no_purchase_uri_reason === 'ERROR_NO_DEVICE_LINKED') {
        await this.$confirm(this.$t('LINKED_DEVICE_REQUIRED'));
        this.$router.push('/link');
        return;
      }

      if (!offer.purchase_uri && !offer.manual_instructions) {
        await this.$alert(offer.no_purchase_uri_reason_translated
          || this.$t(offer.no_purchase_uri_reason));
        return;
      }

      const deviceConfirmed = await this.$confirm(this.currentDevice.name ? `${this.$t('IS_CORRECT_DEVICE')} ${this.currentDevice.model} (${this.currentDevice.name})?` : `${this.$t('IS_CORRECT_DEVICE')} ${this.currentDevice.model}?`)
        .catch(() => {
          if (this.devices.length > 1) {
            return this.$confirm(this.$t('SWITCH_TO_ANOTHER_DEVICE_REDIRECT'))
              .then(() => {
                this.$router.push('/my/devices');
              });
          }

          return Promise.reject();
        })
        .catch(() => this.$confirm(this.$t('DO_YOU_WANT_LINK_ANOTHER_DEVICE'))
          .then(() => {
            this.$router.push('/my/devices');
          }))
        .catch(() => {
          if (!isItselfLinkable()) {
            this.$notify({
              type: 'error',
              text: this.$t('VISIT_FROM_IDEVICE'),
            });
            return;
          }

          this.$notify({
            type: 'success',
            text: this.$t('ORPHANED_LINK_DESC'),
          });

          this.$api.getLinkCode()
            .then((code) => {
              this.$notify({
                type: 'success',
                text: this.$t('DEVICE_IS_BEING_LINKED_RELOAD'),
              });

              this.$store.commit('lt', '');
              this.$api.link({
                code,
                type: 'new',
              }, true)
                .then((data) => {
                  this.$store.commit('lt', data.link_token);
                  window.location = data.profile_service;
                });
            });
        });

      if (deviceConfirmed !== true) {
        return;
      }

      if (offer.manual_instructions) {
        await this.$confirm(`${this.$t('YOU_ARE_ABOUT_TO_BUY_DEV_CERT', {
          name: offer.name,
          amount: offer.price,
          currency: offer.currency,
        })}\n\n${offer.manual_instructions}`, { okText: 'I_MADE_PAYMENT' });

        await this.$confirm(this.$t('P2P_PAYMENT_CONFIRMATION_DESC'));

        this.confirmingOffers.push(offer.id);

        try {
          await this.$api.editP2POffer({
            id: offer.id,
            operation: 'request',
          });

          this.$notify({
            type: 'success',
            text: this.$t('P2P_PAYMENT_CONFIRMATION_REQUEST_SUCCESS'),
          });
        } finally {
          const idx = this.confirmingOffers.indexOf(offer.id);
          this.confirmingOffers.splice(idx, 1);
        }

        return;
      }

      if (offer.purchase_uri) {
        await this.$confirm(this.$t('P2P_CERTIFICATE_BUY_CONFIRM_TEXT'));
        window.location.href = offer.purchase_uri;
      }
    },
    saveP2PSettings() {
      this.savingP2PSettings = true;
      this.$api.editPlusDevAccountP2PSettings(this.p2pSettings)
        .then(() => this.$store.dispatch('getConfig'))
        .then(() => {
          this.configuringP2PSettings = false;
          this.$notify({
            type: 'success',
            text: this.$t('P2P_CONFIGURATION_SUCCESS'),
          });
        })
        .finally(() => {
          this.savingP2PSettings = false;
        });
    },
    async banPurchase(udid) {
      const reason = await this.$prompt(this.$t('P2P_PURCHASE_BAN_WARNING'));
      this.banningPurchases.push(udid);
      this.$api.banunbanDevice({
        udid,
        reason,
      })
        .then(() => {
          this.$notify({
            type: 'success',
            text: this.$t('P2P_PURCHASE_BAN_SUCCESS'),
          });
          this.$api.getP2PPurchases()
            .then((response) => {
              this.purchases = response.data;
            });
        })
        .finally(() => {
          const idx = this.banningPurchases.indexOf(udid);
          this.banningPurchases.splice(idx, 1);
        });
    },
    async unbanPurchase(udid) {
      const reason = '';
      this.unbanningPurchases.push(udid);
      this.$api.banunbanDevice({
        udid,
        reason,
      })
        .then(() => {
          this.$notify({
            type: 'success',
            text: this.$t('P2P_PURCHASE_UNBAN_SUCCESS'),
          });
          this.$api.getP2PPurchases()
            .then((response) => {
              this.purchases = response.data;
            });
        })
        .finally(() => {
          const idx = this.unbanningPurchases.indexOf(udid);
          this.unbanningPurchases.splice(idx, 1);
        });
    },
    async approvePurchase(id) {
      await this.$confirm(this.$t('P2P_PURCHASE_APPROVAL_CONFIRMATION'));

      this.approvingPurchases.push(id);
      try {
        await this.$api.editP2POffer({
          id,
          operation: 'approve',
        });
      } finally {
        const idx = this.approvingPurchases.indexOf(id);
        this.approvingPurchases.splice(idx, 1);

        this.getPurchases();
      }
    },
    async rejectPurchase(id) {
      await this.$confirm(this.$t('P2P_PURCHASE_REJECTION_CONFIRMATION'));

      this.rejectingPurchases.push(id);
      try {
        await this.$api.editP2POffer({
          id,
          operation: 'reject',
        });
      } finally {
        const idx = this.rejectingPurchases.indexOf(id);
        this.rejectingPurchases.splice(idx, 1);

        this.getPurchases();
      }
    },
    getOffers() {
      const currency = this.offersCurrency;
      return this.$api.getP2POffers({ currency })
        .then((response) => {
          if (currency !== this.offersCurrency) {
            return;
          }
          this.offers = response;
        });
    },
    getPurchases() {
      this.$api.getP2PPurchases()
        .then((response) => {
          this.purchases = response.data;
        });
    },
  },
  watch: {
    offersCurrency() {
      this.getOffers();
    },
  },
  async created() {
    const promises = [];

    promises.push(this.getStats()
      .then((stats) => {
        if (!this.p2pSettings.p2pCurrency) {
          // eslint-disable-next-line prefer-destructuring
          this.p2pSettings.p2pCurrency = Object.keys(stats.p2p_currencies)[0];
        }
      }));

    promises.push(this.$store.dispatch('getConfig')
      .then((config) => {
        const plusAccount = config.plus_account;

        if (!plusAccount) {
          return;
        }

        this.p2pSettings.isP2P = plusAccount.is_p2p === 1 ? '1' : '0';
        this.p2pSettings.p2pPrice = plusAccount.p2p_price;
        this.p2pSettings.p2pName = plusAccount.p2p_name;
        this.p2pSettings.p2pPaypalEmail = plusAccount.p2p_paypal_email;
        this.p2pSettings.p2pCurrency = plusAccount.p2p_currency || this.p2pSettings.p2pCurrency;
        this.p2pSettings.p2pManualInstructions = plusAccount.p2p_manual_instructions;
      }));

    promises.push(this.getOffers());

    promises.push(this.getPurchases());

    Promise.allSettled(promises)
      .then(() => {
        this.loading = false;
      });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

b {
  font-weight: 600;
}

.main {
  display: block;
  flex-grow: 1;
}

.intro {
  &__inner {
    margin-right: 5px;
  }

  &__status {
    font-size: inherit;
    font-weight: 400;
  }

  &:not(:first-child) {
    margin-top: 30px;
    margin-bottom: 20px;
  }
}

.text {
  margin-bottom: 8px;

  &::v-deep {
    ul {
      list-style: inside disc;
    }

    li:not(:last-child) {
      margin-bottom: 3px;
    }

    strong {
      font-weight: 700;
    }
  }
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-wrap: wrap;
  }
}

.col {
  &_1 {
    flex-grow: 1;
  }
}

.button {
  font-size: 16px;
}

.h3 {
  margin-top: 20px;
}

.table {
  width: 100%;
  line-height: 20px;
  border: 1px solid #e5e5e5;
  border-left: none;
  border-right: none;
  padding-top: 15px;
  margin-top: 15px;

  &::v-deep {
    strong {
      font-weight: 700;
    }
  }

  tr:first-child td {
    padding-top: 15px;
  }

  tr:last-child td {
    padding-bottom: 15px;
  }

  &_configure {
    border: none;
    padding-top: 0;
    margin-top: 0;

    tr {
      &:first-child td {
        padding-top: 10px;
      }
    }

    .td {
      display: flex;
      justify-content: flex-start;
      padding-bottom: 10px;

      .input,
      .textarea {
        margin-left: 0;
      }
    }
  }
}

.td {
  padding-left: 40px;
}

.cell {
  width: 140px;
  flex: 0 0 140px;
}

.controls {
  margin-top: 5px;

  &__btn {
    margin-top: 10px;
  }

  &__btn:not(:last-child) {
    margin-right: 10px;
  }
}

.table-apps-wrapper {
  @include notebook {
    overflow-x: auto;
    width: 100vw;
    margin-left: -8px;
  }
}

.commands {
  margin-top: 15px;
}

.command {
  padding: 13px 0;
  border-bottom: 1px solid #e5e5e5;

  &:first-child {
    border-top: 1px solid #e5e5e5;
  }

  &__heading {
    font-size: 15px;
    font-weight: 500;
    display: block;
    margin-bottom: 5px;

    &:not(:first-child) {
      margin-top: 10px;
    }
  }

  &__date {
    display: block;
    white-space: nowrap;
    color: $text-secondary;
    font-size: 12px;
    margin-bottom: 5px;
  }
}

.table-apps__date,
.command__date {
  display: block;
  white-space: nowrap;
  color: $text-secondary;
  font-size: 12px;
  margin-bottom: 5px;
}

.table-apps__status,
.command__status {
  font-size: 14px;
  white-space: nowrap;
}

.table-apps__button,
.command__button {
  padding-top: 0px;
  padding-bottom: 1px;
  margin-left: 5px;
}

.table-apps {
  margin-top: 15px;
  text-align: left;
  width: 100%;

  &__head {
    font-weight: 500;
  }

  &__cell {
    padding: 8px;
    vertical-align: middle;

    &_nw {
      white-space: nowrap;
    }

    &_right {
      text-align: right;
    }
  }

  &__row {
    border-bottom: 1px solid #e5e5e5;

    &:hover {
      background-color: var(--lighter-primary);
    }
  }
}

.buy {
  margin-top: 20px;
}

.input,
.textarea {
  background: var(--tertiary-bg);
  border: 1px solid var(--tertiary-accent);
  border-radius: 2px;
  padding: 4px 6px;
  margin-left: auto;
  flex: 0 0 150px;
  width: 150px;
  font-size: 14px;
  font-family: "IBM Plex Sans", sans-serif;
  color: var(--text-primary);

  &::placeholder {
    font-family: "IBM Plex Sans", sans-serif;
    color: var(--text-primary);
  }
}

.textarea {
  max-width: 300px;
  flex: 0 0 auto;
  width: 100%;
  resize: none;
}

.status {
  font-weight: 700;
  text-align: center;
  margin-top: 20px;
}

.currency-select {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 20px;
}
</style>
